import React, { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { useSnackbar } from '../contexts/SnackbarProvider';
import { useForm, Controller } from "react-hook-form";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TextField
} from "@mui/material";

import { Delete, Add } from "@mui/icons-material";
import AutocompleteInput from "../components/AutocompleteInput";

const VariantCombineModal = ({ open, variant, onClose, hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const { control, handleSubmit, setValue, watch, reset } = useForm({
        defaultValues: { components: [] },
    });
    const components = watch("components");

    // 初期値取得
    useEffect(() => {
        if (!variant) return;

        const loadComponents = async () => {
            try {
                const res = await axiosInstance.get(`/product/variants/${variant.variant_id}/combine`);
                // APIから取得した組み合わせで初期化
                reset({
                    components: res.data.map(c => ({
                        variant_id: c.variant_id,
                        sku: c.sku,       // 表示用
                        quantity: c.quantity
                    }))
                });
            } catch (err) {
                console.error(err);
                reset({ components: [] });
            }
        };

        loadComponents();
    }, [variant, reset]);

    const addRow = () => {
        setValue("components", [...components, { variant_id: "", sku: "", quantity: 1 }]);
    };

    const removeRow = (index) => {
        setValue("components", components.filter((_, i) => i !== index));
    };

    const save = async (data) => {
        try {
            // variant_idのみサーバーに送る
            const payload = data.components.map(c => ({
                variant_id: c.variant_id,
                quantity: c.quantity
            }));
            await axiosInstance.post(`/product/variants/${variant.variant_id}/combine`, payload);
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || "保存に失敗しました", 'error');
        }
    };

    return (
        <Dialog open={open} maxWidth="md" fullWidth>
            <DialogTitle>セット商品組合せ {variant?.sku}</DialogTitle>
            <DialogContent>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>品番</TableCell>
                            <TableCell width={120}>数量</TableCell>
                            <TableCell width={60}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {components.map((c, i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    <Controller
                                        name={`components.${i}.sku`}
                                        control={control}
                                        rules={{ required: "品番は必須です" }}
                                        render={({ field }) => (
                                            <AutocompleteInput
                                                hasLimit={hasLimit}
                                                value={field.value || ""}
                                                isEditMode={true}
                                                fetchSuggestions={async (keyword) => {
                                                    try {
                                                        const res = await axiosInstance.post("/product/sku_suggest", { keyword });
                                                        return (res.data || []).filter(v => v.variant_id !== variant.variant_id);
                                                    } catch {
                                                        return [];
                                                    }
                                                }}
                                                onChange={(e, idx, selected) => {
                                                    field.onChange(selected?.sku || e?.target?.value || "");
                                                    setValue(`components.${i}.variant_id`, selected?.variant_id || "");
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Controller
                                        name={`components.${i}.quantity`}
                                        control={control}
                                        defaultValue={1}
                                        rules={{
                                            required: "数量は必須です",
                                            validate: value => {
                                                if (value === '') return true;
                                                if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                if (Number(value) < 0) return '0以上の数字を入力してください';
                                                if (value.length > 9) return '9桁以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                type="number"
                                                {...field}
                                                inputProps={{ min: 0, maxLength: 9 }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => removeRow(i)}>
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Button startIcon={<Add />} onClick={addRow} sx={{ mt: 2 }}>
                    バリアント追加
                </Button>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>キャンセル</Button>
                <Button variant="contained" onClick={handleSubmit(save)}>
                    保存
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VariantCombineModal;